import * as React from "react";

import { useId } from "@fluentui/react-components";
import type { AppButtonState } from "../components/AppButton/next";
import { hiddenContentClassName } from "@1js/acui-common";
import { checkExperiment, ribbonExperiments } from "@1js/acui-experiments";
import { AppContext } from "@1js/acui-context";

export type StateWithAriaDescriptionProperties = {
  root: {
    "aria-describedby"?: string;
    "aria-label"?: string;
    children?: AppButtonState["root"]["children"];
  };
};

export function useAriaDescription<
  T extends StateWithAriaDescriptionProperties,
>(state: T, ariaDescription?: string) {
  // Create a unique ID for the span that won't change later
  // Needs to be run unconditionally since it's a hook
  const uniqueID = React.useRef(useId()).current;
  const context = React.useContext(AppContext);
  if (ariaDescription) {
    // Prepend the ID to the aria-describedby attribute on the root button
    const ariaDescribedBy = state.root["aria-describedby"] || "";
    const ariaLabel = state.root["aria-label"] || "";
    state.root["aria-describedby"] = uniqueID + " " + ariaDescribedBy;
    // Add the span with the description to the existing children, if any
    const children = state.root.children
      ? [<React.Fragment key={uniqueID}>{state.root.children}</React.Fragment>]
      : [];
    children.push(
      <span
        className={hiddenContentClassName}
        id={uniqueID}
        key={`${uniqueID}-ariaDescription`}
      >
        {!!checkExperiment(
          context,
          ribbonExperiments.isDescriptionReadoutWithoutLabelEnabled
        )
          ? getDescriptionForReadout(ariaDescription, ariaLabel)
          : ariaDescription}
      </span>
    );
    state.root.children = <>{children}</>;
  }
}

// If an aria-label attribute already exists, remove label from description
// to avoid duplicative readout. (Removes first occurrence only)
function getDescriptionForReadout(description: string, label: string) {
  // ADO 9361564 - description is sometimes not a string
  if (
    label !== "" &&
    typeof description === "string" &&
    description.startsWith(label)
  ) {
    return description.replace(label, "").trim();
  }
  return description;
}
